export type BridgeMessage =
  | {
      type: 'auth-bridge:success';
      /**
       * Serialized OAuth credential (output of AuthCredential.toJSON()).
       * The parent reconstructs it via <Provider>.credentialFromJSON()
       * and calls signInWithCredential() to establish a session at its
       * own origin. NOT a Firebase ID token — that would be useless
       * because it's bound to the bridge origin's project session.
       */
      credentialJson: Record<string, unknown>;
      providerId: string;
    }
  | {
      type: 'auth-bridge:error';
      error: 'auth_failed';
    };

// Module-level latch: the bridge is single-use. Each popup instance loads the
// module fresh, so this is per-popup. Defense-in-depth against orchestrator
// bugs that would otherwise re-deliver tokens.
let hasSent = false;

function isCanonicalOrigin(target: string): boolean {
  let u: URL;
  try {
    u = new URL(target);
  } catch {
    return false;
  }
  // Only http/https schemes — postMessage with javascript:, data:, file:, blob:
  // would otherwise extract an unexpected origin per spec.
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
  // http:// is only legitimate for localhost in dev. Anywhere else, require https.
  if (u.protocol === 'http:' && u.hostname !== 'localhost') return false;
  // Origin must be the bare origin — no path, query, fragment, or userinfo.
  if (u.pathname !== '/' && u.pathname !== '') return false;
  if (u.search || u.hash) return false;
  if (u.username || u.password) return false;
  // Canonical-origin check: the input must equal the parsed origin.
  // Rejects trailing-slash variants like 'https://x.com/' (parsed.origin = 'https://x.com').
  if (target !== u.origin) return false;
  return true;
}

export function sendToParent(message: BridgeMessage, targetOrigin: string): void {
  if (hasSent) {
    throw new Error('Bridge has already delivered a message; refusing duplicate send');
  }
  if (!targetOrigin || targetOrigin === '*') {
    throw new Error('Refusing to postMessage with empty or wildcard targetOrigin');
  }
  if (!isCanonicalOrigin(targetOrigin)) {
    throw new Error('Refusing to postMessage with non-canonical targetOrigin');
  }
  const opener = window.opener as Window | null;
  if (!opener) {
    throw new Error('No opener window — bridge must be opened as a popup');
  }
  if (opener === window) {
    throw new Error('Opener is self — refusing to send tokens to current window');
  }
  hasSent = true;
  opener.postMessage(message, targetOrigin);
}

/** Test-only: reset the single-use latch between Vitest cases. */
export function _resetForTesting(): void {
  hasSent = false;
}
